import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getExercises, deleteExercise } from '../api/exercises';
import { getTags } from '../api/tags';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';
import TagBadge from '../components/common/TagBadge';
import DifficultyStars from '../components/common/DifficultyStars';
import ConfirmModal from '../components/common/ConfirmModal';

export default function ExerciseListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin';
  const canEdit = user?.role === 'admin' || user?.role === 'trainer' || isSuperAdmin;

  const [exercises, setExercises] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([getExercises(), getTags()])
      .then(([ex, t]) => { setExercises(ex); setTags(t); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [user?.id]); // eslint-disable-line

  async function handleDelete() {
    try {
      await deleteExercise(confirmDelete.id);
      setExercises((prev) => prev.filter((e) => e.id !== confirmDelete.id));
      setSuccess('Übung gelöscht.');
    } catch (e) {
      setError(e.message);
    } finally {
      setConfirmDelete(null);
    }
  }

  const filtered = exercises.filter((ex) => {
    const matchesSearch = !search || ex.name.toLowerCase().includes(search.toLowerCase());
    const matchesTag = !filterTag || ex.tags?.some((t) => String(t.id) === filterTag);
    return matchesSearch && matchesTag;
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 fw-bold">
          <i className="bi bi-activity me-2 text-primary" />
          Übungen
        </h4>
        {canEdit && (
          <Link className="btn btn-primary" to="/exercises/new">
            <i className="bi bi-plus-lg me-1" />
            Neue Übung
          </Link>
        )}
      </div>

      <AlertBanner message={error} type="danger" onDismiss={() => setError('')} />
      <AlertBanner message={success} type="success" onDismiss={() => setSuccess('')} />

      <div className="card card-body mb-4 p-3">
        <div className="row g-2">
          <div className="col-sm-5">
            <input
              type="search"
              className="form-control form-control-sm"
              placeholder="Suche nach Name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="col-sm-3">
            <select
              className="form-select form-select-sm"
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
            >
              <option value="">Alle Tags</option>
              {tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filtered.length === 0 ? (
        <div className="text-center text-muted py-5">
          <i className="bi bi-activity fs-1 d-block mb-2" />
          Keine Übungen gefunden.
          {canEdit && (
            <div className="mt-3">
              <Link className="btn btn-primary" to="/exercises/new">Erste Übung erstellen</Link>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Desktop: Tabelle */}
          <div className="table-responsive d-none d-md-block">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Schwierigkeit</th>
                  <th>Equipment</th>
                  <th>Tags</th>
                  <th className="text-end">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((ex) => (
                  <tr
                    key={ex.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/exercises/${ex.id}`)}
                  >
                    <td className="fw-semibold">
                      {ex.name}
                      {isSuperAdmin && ex.is_global && <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.65rem' }}>Global</span>}
                    </td>
                    <td><DifficultyStars value={ex.difficulty} /></td>
                    <td className="text-muted small">{ex.equipment_needed || '—'}</td>
                    <td>{ex.tags?.map((t) => <TagBadge key={t.id} tag={t} />)}</td>
                    <td className="text-end" onClick={(e) => e.stopPropagation()}>
                      {(isSuperAdmin || canEdit) && (
                        <>
                          <button
                            className="btn btn-sm btn-outline-secondary me-1"
                            title="Bearbeiten"
                            onClick={() => navigate(`/exercises/${ex.id}/edit`)}
                          >
                            <i className="bi bi-pencil" />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            title="Löschen"
                            onClick={() => setConfirmDelete(ex)}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: Karten */}
          <div className="d-md-none">
            {filtered.map((ex) => (
              <div
                key={ex.id}
                className="card mb-2"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/exercises/${ex.id}`)}
              >
                <div className="card-body py-2 px-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="fw-semibold">
                      {ex.name}
                      {isSuperAdmin && ex.is_global && (
                        <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.65rem' }}>Global</span>
                      )}
                    </div>
                    {(isSuperAdmin || canEdit) && (
                      <div className="d-flex gap-1 ms-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          title="Bearbeiten"
                          onClick={() => navigate(`/exercises/${ex.id}/edit`)}
                        >
                          <i className="bi bi-pencil" />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Löschen"
                          onClick={() => setConfirmDelete(ex)}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
                    <DifficultyStars value={ex.difficulty} />
                    {ex.equipment_needed && (
                      <span className="text-muted small">{ex.equipment_needed}</span>
                    )}
                    {ex.tags?.map((t) => <TagBadge key={t.id} tag={t} />)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <ConfirmModal
        show={!!confirmDelete}
        title="Übung löschen"
        message={`Übung "${confirmDelete?.name}" wirklich löschen?`}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
